import type * as PIXI from 'pixi.js';

import type { VideoData } from '../video-data';
import type { OpaqueVideoData } from '../video-data/types';
import type { BufferingMode } from '../enums';
import type { SceneGroup } from '../core/SceneGroup';
import type { Scene } from '../core/scene/Scene';
import type { Subscene } from '../core/scene/Subscene';
import type { AspectRatio } from '../aspectRatioConfig';
import type Luminary from '../index';

export * from './units';

export type MixinBase<T = any> = new (...args: any[]) => T;
export type GetConstructorParameters<TBase> = TBase extends new (props: infer P) => any ? P : never;
export type GetInstance<TBase> = TBase extends new (...args: any[]) => infer I ? I : never;
export type GetMultipleConstructorParameters<TBase> = TBase extends new (...props: infer P) => any
    ? P
    : never;
export type MergeConstructors<A, B> = new (
    ...props: GetMultipleConstructorParameters<A> & GetMultipleConstructorParameters<B>
) => GetInstance<A> & GetInstance<B>;
export type PublicConstructor<T> = new (props: GetConstructorParameters<T>) => T;

type Entries<T> = {
    [K in keyof T]: [K, T[K]];
}[keyof T][];

export function typedEntries<T>(obj: T): Entries<T> {
    return Object.entries(obj as Record<string, unknown>) as Entries<T>;
}

type primitive = string | number | boolean | undefined | null;
type DeepReadonly<T> = T extends primitive ? T : DeepReadonlyObject<T>;
export type DeepReadonlyObject<T> = {
    readonly [P in keyof T]: DeepReadonly<T[P]>;
};

export type RgbString = `rgb(${number},${number},${number})`;
export type RgbaString = `rgba(${number},${number},${number},${number})`;

export type Resolution = {
    height: number;
    width: number;
};
export type BoundingBox = {
    x: number;
    width: number;
    y: number;
    height: number;
};
export type Transformation = {
    anchor: {
        x: number;
        y: number;
    };
    position: Point;
};
export type Point = {
    x: number;
    y: number;
};

/* A record of asset properties which use template references and their corresponding template references. */
export type AssetTemplateReferenceMap = Record<string, string>;

/* A record of scene asset names with their corresponding asset template reference map. This type is also used in web.
 * One of its use cases is to enable our color shuffle functionality. */
export type SceneTemplateReferenceMap = Record<string, AssetTemplateReferenceMap>;

export const FETCH_ON_BUFFER = 'onBuffer';

/**
 * prevents assets from loading if false. A boolean will set all options to that value
 */
export type FetchResources =
    | boolean
    | {
          /**
           * prevents transitions from loading
           */
          transitions?: boolean;
          /**
           * prevents images, videos, icons, lottie backgrounds and html compositions from loading
           */
          media?: boolean | typeof FETCH_ON_BUFFER;
          /**
           * prevents fonts from loading
           */
          fonts?: boolean;
          /**
           * prevents audio from loading
           */
          audio?: boolean;
      };

/** @internal FETCH_ON_BUFFER means yes; only MediaAsset defers to buffer time, in its own _shouldLoad. */
export function shouldLoadMedia(fetchResources: FetchResources): boolean {
    if (typeof fetchResources === 'boolean') {
        return fetchResources;
    }
    return fetchResources.media === true || fetchResources.media === FETCH_ON_BUFFER;
}

export type AlignChildren = 'left' | 'center' | 'right';

export type SceneCategory = (typeof VideoData.ALL_CATEGORIES)[number];
export type MediaSize = (typeof VideoData.ALL_MEDIA_SIZES)[number];

export type Coordinate = {
    /**
     * x-coord, relative to the resolution
     */
    x: number;
    /**
     * y-coord, relative to the resolution
     */
    y: number;
};

export type TextEditingOptions = {
    minSize: number;
};

/**
 * A boolean will act as if all options have been set to that value
 */
export type DestroyOptions = boolean | PIXI.DestroyOptions;

/**
 * The Template bundle type only represents a subset of the actual template bundle type. The actual object has
 * additional properties which deal with template metadata used by external services such as the template and
 * scene design editor. However, in the context of the luminary API, we only need the scene designs and template data
 * which is expressed in this type.
 */
export type TemplateBundle = {
    sceneDesigns: {
        [key: string]: { [K in AspectRatio]: OpaqueVideoData | null };
    };
    templateData: OpaqueVideoData;
};

export type Interpolation = (typeof VideoData.ALL_INTERPOLATION_PRESETS)[number];

/**
 * Defines the common interface for objects that are the core pieces to luminary's structure
 */
export interface LuminaryObject {
    /**
     * When seeking and after timelines have been updated, propagate a request to buffer data down the hierarchy.
     */
    _propagateBuffering(bufferingMode: BufferingMode): Promise<void>;

    /**
     * This propagates play down the luminary hierarchy. This should happen after timeline play
     * has been called.
     */
    _propagatePlay(): void;

    /**
     * This propagates pause down the luminary hierarchy. This should happen after timeline pause
     * has been called.
     */
    _propagatePause(): void;

    /**
     * Returns true if all assets (deeply) within this object have finished loading, or have
     * failed to load. If any assets are still loading or retrying a load this will be false.
     */
    get areAssetsDoneTryingToLoad(): boolean;

    /**
     * Returns true once ready for playback. This is similar to areAssetsDoneTryingToLoad, but
     * in addition it considers shouldLoad and will immediately consider an asset ready if it
     * has loading disabled.
     */
    get canPlay(): boolean;

    /**
     * Returns true if all assets (deeply) within this object have successfully loaded.
     */
    get isLoaded(): boolean;

    /**
     * Used to propagate the canPlay event upwards. Each object needs to forward the event to
     * its luminary container
     */
    _emitAssetsCanPlay(): void;
}

/**
 * Defines the interface for asset objects within luminary. These are things like text, media,
 * etc. which represent a visual component in the rendered view.
 */
export interface AssetInterface {
    /**
     * Returns true if the asset is done trying to load. This means it either finished loading or
     * it failed to load.
     */
    get isDoneTryingToLoad(): boolean;

    /**
     * Returns true once ready for playback. This is similar to isDoneTryingToLoad, but
     * in addition it considers shouldLoad and will immediately be ready if the asset is
     * disabled from loading.
     */
    get canPlay(): boolean;

    /**
     * Returns true if the asset has successfully loaded.
     */
    get isLoaded(): boolean;

    /**
     * Returns true if the asset failed to load.
     */
    get failedToLoad(): boolean;

    /**
     * Requests that this asset buffers some data at the given time. Not all assets need to buffer data.
     * Some examples are buffering video and caching textures.
     */
    _bufferAtTime(time: number, bufferingMode: BufferingMode): void;

    /**
     * Most assets don't need to support this. This is only needed for assets where we can't
     * fully control individual frame updates during playback eg. MediaAsset
     */
    _propagatePlay(): void;

    /**
     * Most assets don't need to support this. This is only needed for assets where we can't
     * fully control individual frame updates during playback eg. MediaAsset
     */
    _propagatePause(): void;

    /**
     * Used to emit the assetCanPlay event on the asset and its luminary container. This event
     * should only be emitted once per load cycle (e.g. if a source url changes then it would
     * emit again after the new source loads).
     */
    _emitAssetCanPlay(): void;

    /**
     * Returns the "luminary object" which directly contains this asset. This is either a
     * SceneGroup, Scene or Subscene.
     */
    get luminaryParent(): Luminary | SceneGroup | Scene | Subscene | null;
}

/**
 * Base interface for lifecycle-aware components. Lifecycle refers to events or actions that need
 * to propagate through the core structure of the luminary hierarchy. For example, 'canPlay' event.
 */
interface BaseLifecycleAware {
    canPlay: boolean;
    isLoaded: boolean;
    isDoneTryingToLoad: boolean;
    destroyed: boolean;
    _propagatePlay(): void;
    _propagatePause(): void;
}

/**
 * Lifecycle-aware component that uses autoDraw for update propagation (BaseAsset descendants).
 */
export interface DrawableLifecycleAware extends BaseLifecycleAware {
    autoDraw: boolean;
}

/**
 * Lifecycle-aware component that uses autoUpdate for update propagation (TimeAwareBase non-BaseAsset descendants).
 */
export interface UpdatableLifecycleAware extends BaseLifecycleAware {
    autoUpdate: boolean;
}

/**
 * Union type for all lifecycle-aware components.
 */
export type LifecycleAware = DrawableLifecycleAware | UpdatableLifecycleAware;

/**
 * After Effects–style track matte type. `alpha` reveals this asset through the matte's opaque area;
 * `alpha-inverted` hides this asset within it (carving holes).
 */
export type TrackMatteType = 'alpha' | 'alpha-inverted';

/**
 * A reference to another asset (by name) used as a track matte for this asset, together with the
 * matte type. Names (not ids) are stable across scene-design instantiations.
 */
export interface TrackMatte {
    assetName: string;
    type: TrackMatteType;
}
